/**
 * ChunkStreamer - Data-driven chunk streaming system
 * 
 * EXPANSION 1: Road to Frostholm
 * - Biome-based terrain coloring
 * - New landmarks: Watchtower, Bridge, Camp
 * - Frostholm settlement buildings
 * - Snow-covered variants
 * 
 * Each chunk provides:
 * - Visible terrain with biome-appropriate color
 * - Walkable collision (physics collider)
 * - Props positioned ON the terrain surface (Y=0)
 */

import { useMemo } from 'react';
import { RigidBody, CuboidCollider } from '@react-three/rapier';
import * as THREE from 'three';
import { 
  WORLD_CONFIG, 
  WORLD_CHUNKS, 
  getActiveChunks,
  getBiomeForZ 
} from '../../../config/midgardData';

import WorldRoad, { TownGate, FrostGate, StoneBridge } from './WorldRoad';
import StandingStonesLandmark from './StandingStonesLandmark';

// Chunk terrain constants
const CHUNK_SIZE = WORLD_CONFIG.chunkSize; // 64 units
const TERRAIN_Y = 0; // Ground level
const THORNHAVEN_HALF = (WORLD_CONFIG.thornhavenTerrainSize || 150) / 2 - 1; // 74

// Reusable geometries for performance (instancing)
const TREE_TRUNK_GEO = new THREE.CylinderGeometry(0.3, 0.4, 3, 6);
const TREE_FOLIAGE_GEO = new THREE.ConeGeometry(2, 4, 6);
const TREE_TOP_GEO = new THREE.ConeGeometry(1.5, 3, 6);
const ROCK_GEO = new THREE.DodecahedronGeometry(0.8, 0);
const CHUNK_PLANE_GEO = new THREE.PlaneGeometry(CHUNK_SIZE, CHUNK_SIZE);

// Reusable materials for performance
const MATERIALS = {
  treeTrunk: new THREE.MeshStandardMaterial({ color: '#5c4033', roughness: 0.9 }),
  treeFoliage: new THREE.MeshStandardMaterial({ color: '#2d5a27', roughness: 0.8 }),
  treeTop: new THREE.MeshStandardMaterial({ color: '#3a6b33', roughness: 0.8 }),
  snowTreeFoliage: new THREE.MeshStandardMaterial({ color: '#4a6a5a', roughness: 0.8 }),
  snowTreeTop: new THREE.MeshStandardMaterial({ color: '#5a7a6a', roughness: 0.8 }),
  rock: new THREE.MeshStandardMaterial({ color: '#6b7280', roughness: 0.95 }),
  snowRock: new THREE.MeshStandardMaterial({ color: '#8a9a9a', roughness: 0.9 }),
  stone: new THREE.MeshStandardMaterial({ color: '#5a5a5a', roughness: 0.85 }),
  wood: new THREE.MeshStandardMaterial({ color: '#6b5040', roughness: 0.8 }),
  darkWood: new THREE.MeshStandardMaterial({ color: '#4a3a2a', roughness: 0.85 }),
  thatch: new THREE.MeshStandardMaterial({ color: '#8a7a5a', roughness: 0.95 }),
  snowThatch: new THREE.MeshStandardMaterial({ color: '#9a9a8a', roughness: 0.9 }),
  fire: new THREE.MeshStandardMaterial({ color: '#ff6600', emissive: '#ff3300', emissiveIntensity: 0.5 }),
};

/**
 * ChunkTerrain - Visible terrain plane with physics collision
 * Uses biome system for terrain color based on Z coordinate
 */
function ChunkTerrain({ chunkX, chunkZ, biome }) {
  const centerX = chunkX * CHUNK_SIZE;
  const centerZ = chunkZ * CHUNK_SIZE;
  
  // Get terrain color from biome
  const biomeData = getBiomeForZ(centerZ);
  const color = biomeData?.terrainColor || '#3D5C1F';
  
  // Slightly below Thornhaven terrain for smooth overlap
  const terrainY = -0.02;
  
  // Create material with biome color
  const material = useMemo(() => 
    new THREE.MeshStandardMaterial({ color, roughness: 0.95 }), 
    [color]
  );
  
  return (
    <group position={[centerX, terrainY, centerZ]}>
      {/* Physics collision for this chunk */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider 
          args={[CHUNK_SIZE / 2, 0.5, CHUNK_SIZE / 2]} 
          position={[0, -0.5, 0]} 
        />
      </RigidBody>
      
      {/* Visible terrain plane */}
      <mesh 
        rotation={[-Math.PI / 2, 0, 0]} 
        receiveShadow 
        position={[0, -0.01, 0]}
        geometry={CHUNK_PLANE_GEO}
        material={material}
      />
    </group>
  );
}

/**
 * Tree - Regular green tree
 */
function SimpleTree({ position }) {
  const [x, , z] = position;
  
  return (
    <group position={[x, TERRAIN_Y, z]}>
      <mesh position={[0, 1.5, 0]} castShadow geometry={TREE_TRUNK_GEO} material={MATERIALS.treeTrunk} />
      <mesh position={[0, 4, 0]} castShadow geometry={TREE_FOLIAGE_GEO} material={MATERIALS.treeFoliage} />
      <mesh position={[0, 5.5, 0]} castShadow geometry={TREE_TOP_GEO} material={MATERIALS.treeTop} />
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[0.4, 1.5, 0.4]} position={[0, 1.5, 0]} />
      </RigidBody>
    </group>
  );
}

/**
 * Snow-covered Tree
 */
function SnowTree({ position }) {
  const [x, , z] = position;
  
  return (
    <group position={[x, TERRAIN_Y, z]}>
      <mesh position={[0, 1.5, 0]} castShadow geometry={TREE_TRUNK_GEO} material={MATERIALS.treeTrunk} />
      <mesh position={[0, 4, 0]} castShadow geometry={TREE_FOLIAGE_GEO} material={MATERIALS.snowTreeFoliage} />
      <mesh position={[0, 5.5, 0]} castShadow geometry={TREE_TOP_GEO} material={MATERIALS.snowTreeTop} />
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[0.4, 1.5, 0.4]} position={[0, 1.5, 0]} />
      </RigidBody>
    </group>
  );
}

/**
 * Rock - Regular rock
 */
function SimpleRock({ position }) {
  const [x, , z] = position;
  
  return (
    <group position={[x, TERRAIN_Y, z]}>
      <mesh position={[0, 0.3, 0]} castShadow receiveShadow geometry={ROCK_GEO} material={MATERIALS.rock} />
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[0.6, 0.4, 0.6]} position={[0, 0.4, 0]} />
      </RigidBody>
    </group>
  );
}

/**
 * Snow-covered Rock
 */
function SnowRock({ position }) {
  const [x, , z] = position;
  
  return (
    <group position={[x, TERRAIN_Y, z]}>
      <mesh position={[0, 0.3, 0]} castShadow receiveShadow geometry={ROCK_GEO} material={MATERIALS.snowRock} />
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[0.6, 0.4, 0.6]} position={[0, 0.4, 0]} />
      </RigidBody>
    </group>
  );
}

/**
 * Ruined Watchtower - Ancient stone tower
 */
function RuinedWatchtower({ position }) {
  const [x, , z] = position;
  
  return (
    <group position={[x, TERRAIN_Y, z]}>
      {/* Base - wider stone foundation */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[3, 1, 3]} position={[0, 1, 0]} />
      </RigidBody>
      <mesh position={[0, 1, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[3, 3.5, 2, 8]} />
        <meshStandardMaterial color="#5a5a5a" roughness={0.9} />
      </mesh>
      
      {/* Main tower - partially ruined */}
      <mesh position={[0, 4.5, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[2.5, 2.8, 5, 8]} />
        <meshStandardMaterial color="#6a6a6a" roughness={0.85} />
      </mesh>
      
      {/* Broken top section */}
      <mesh position={[0.5, 7.5, 0]} castShadow receiveShadow rotation={[0.1, 0, 0.15]}>
        <cylinderGeometry args={[2, 2.2, 2, 8, 1, false, 0, Math.PI * 1.5]} />
        <meshStandardMaterial color="#5a5a5a" roughness={0.9} />
      </mesh>
      
      {/* Scattered rubble */}
      <mesh position={[4, 0.3, 2]} castShadow>
        <boxGeometry args={[1.5, 0.6, 1]} />
        <meshStandardMaterial color="#707070" roughness={0.95} />
      </mesh>
      <mesh position={[-3, 0.2, 3]} castShadow>
        <boxGeometry args={[1, 0.4, 0.8]} />
        <meshStandardMaterial color="#606060" roughness={0.95} />
      </mesh>
    </group>
  );
}

/**
 * River - Visual water plane (no physics - bridge provides walkway)
 */
function River({ from, to }) {
  const length = Math.abs(to.x - from.x) || Math.abs(to.z - from.z);
  const centerX = (from.x + to.x) / 2;
  const centerZ = (from.z + to.z) / 2;
  const isHorizontal = Math.abs(to.x - from.x) > Math.abs(to.z - from.z);
  
  return (
    <mesh 
      position={[centerX, -0.3, centerZ]} 
      rotation={[-Math.PI / 2, 0, isHorizontal ? 0 : Math.PI / 2]}
      receiveShadow
    >
      <planeGeometry args={[length, 8]} />
      <meshStandardMaterial 
        color="#3a5a7a" 
        roughness={0.2}
        metalness={0.3}
        transparent
        opacity={0.8}
      />
    </mesh>
  );
}

/**
 * Abandoned Camp - Tent remains and extinguished fire
 */
function AbandonedCamp({ position }) {
  const [x, , z] = position;
  
  return (
    <group position={[x, TERRAIN_Y, z]}>
      {/* Collapsed tent frame */}
      <mesh position={[0, 0.8, 0]} rotation={[0.3, 0, 0.1]} castShadow>
        <coneGeometry args={[2, 1.5, 4]} />
        <meshStandardMaterial color="#7a6a5a" roughness={0.9} />
      </mesh>
      
      {/* Fallen pole */}
      <mesh position={[2, 0.15, 1]} rotation={[0, 0.5, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.08, 0.08, 3, 6]} />
        <meshStandardMaterial color="#5a4030" roughness={0.95} />
      </mesh>
      
      {/* Cold fire pit */}
      <mesh position={[-2, 0.1, -1]} receiveShadow>
        <cylinderGeometry args={[0.8, 1, 0.2, 8]} />
        <meshStandardMaterial color="#2a2a2a" roughness={1} />
      </mesh>
      
      {/* Scattered logs */}
      <mesh position={[-2.5, 0.15, -0.5]} rotation={[0, 0.3, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.15, 0.12, 1, 6]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.95} />
      </mesh>
      
      {/* Rocks around fire pit */}
      <mesh position={[-1.3, 0.15, -1.2]} castShadow>
        <dodecahedronGeometry args={[0.25, 0]} />
        <meshStandardMaterial color="#5a5a5a" roughness={0.95} />
      </mesh>
      <mesh position={[-2.6, 0.12, -0.8]} castShadow>
        <dodecahedronGeometry args={[0.2, 0]} />
        <meshStandardMaterial color="#606060" roughness={0.95} />
      </mesh>
    </group>
  );
}

/**
 * Rune Stone - Small carved stone marker
 */
function RuneStone({ position }) {
  const [x, , z] = position;
  
  return (
    <group position={[x, TERRAIN_Y, z]}>
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[0.4, 1, 0.2]} position={[0, 1, 0]} />
      </RigidBody>
      <mesh position={[0, 1, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.8, 2, 0.3]} />
        <meshStandardMaterial color="#5a6a6a" roughness={0.85} />
      </mesh>
      {/* Rune markings (simplified) */}
      <mesh position={[0, 1.2, 0.16]} castShadow>
        <boxGeometry args={[0.5, 0.08, 0.02]} />
        <meshStandardMaterial color="#3a4a4a" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.9, 0.16]} rotation={[0, 0, 0.5]} castShadow>
        <boxGeometry args={[0.4, 0.06, 0.02]} />
        <meshStandardMaterial color="#3a4a4a" roughness={0.9} />
      </mesh>
    </group>
  );
}

/**
 * Frost Longhouse - Frostholm residential building
 */
function FrostLonghouse({ position, rotation = 0 }) {
  const [x, , z] = position;
  
  return (
    <group position={[x, TERRAIN_Y, z]} rotation={[0, rotation, 0]}>
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[4, 2, 2.5]} position={[0, 2, 0]} />
      </RigidBody>
      {/* Main structure */}
      <mesh position={[0, 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[8, 4, 5]} />
        <meshStandardMaterial color="#5a4a3a" roughness={0.85} />
      </mesh>
      {/* Roof */}
      <mesh position={[0, 4.8, 0]} rotation={[0, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[9, 0.8, 6]} />
        <meshStandardMaterial color="#7a7a6a" roughness={0.9} />
      </mesh>
      {/* Roof peak */}
      <mesh position={[0, 5.5, 0]} rotation={[Math.PI/2, 0, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.1, 9.5, 6]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.9} />
      </mesh>
      {/* Door */}
      <mesh position={[0, 1.2, 2.51]} castShadow>
        <boxGeometry args={[1.2, 2.4, 0.1]} />
        <meshStandardMaterial color="#3a2a1a" roughness={0.9} />
      </mesh>
    </group>
  );
}

/**
 * Frost Hall - Frostholm main gathering hall
 */
function FrostHall({ position, rotation = 0 }) {
  const [x, , z] = position;
  
  return (
    <group position={[x, TERRAIN_Y, z]} rotation={[0, rotation, 0]}>
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[6, 3, 4]} position={[0, 3, 0]} />
      </RigidBody>
      {/* Main structure */}
      <mesh position={[0, 3, 0]} castShadow receiveShadow>
        <boxGeometry args={[12, 6, 8]} />
        <meshStandardMaterial color="#5a4a3a" roughness={0.85} />
      </mesh>
      {/* Roof */}
      <mesh position={[0, 6.8, 0]} castShadow receiveShadow>
        <boxGeometry args={[13, 1, 9]} />
        <meshStandardMaterial color="#8a8a7a" roughness={0.9} />
      </mesh>
      {/* Roof ridge */}
      <mesh position={[0, 7.8, 0]} rotation={[Math.PI/2, 0, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.15, 13.5, 6]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.9} />
      </mesh>
      {/* Main entrance */}
      <mesh position={[0, 1.8, 4.01]} castShadow>
        <boxGeometry args={[2, 3.6, 0.1]} />
        <meshStandardMaterial color="#3a2a1a" roughness={0.9} />
      </mesh>
      {/* Decorative columns */}
      <mesh position={[-1.5, 2, 4.2]} castShadow>
        <cylinderGeometry args={[0.2, 0.25, 4, 6]} />
        <meshStandardMaterial color="#6a5a4a" roughness={0.85} />
      </mesh>
      <mesh position={[1.5, 2, 4.2]} castShadow>
        <cylinderGeometry args={[0.2, 0.25, 4, 6]} />
        <meshStandardMaterial color="#6a5a4a" roughness={0.85} />
      </mesh>
    </group>
  );
}

/**
 * Frost House - Smaller Frostholm dwelling
 */
function FrostHouse({ position, rotation = 0 }) {
  const [x, , z] = position;
  
  return (
    <group position={[x, TERRAIN_Y, z]} rotation={[0, rotation, 0]}>
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[2.5, 1.5, 2]} position={[0, 1.5, 0]} />
      </RigidBody>
      {/* Main structure */}
      <mesh position={[0, 1.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[5, 3, 4]} />
        <meshStandardMaterial color="#5a4a3a" roughness={0.85} />
      </mesh>
      {/* Roof */}
      <mesh position={[0, 3.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[5.5, 0.6, 4.5]} />
        <meshStandardMaterial color="#7a7a6a" roughness={0.9} />
      </mesh>
      {/* Door */}
      <mesh position={[0, 1, 2.01]} castShadow>
        <boxGeometry args={[1, 2, 0.1]} />
        <meshStandardMaterial color="#3a2a1a" roughness={0.9} />
      </mesh>
    </group>
  );
}

/**
 * Fire Pit - Central gathering fire (Frostholm)
 */
function FirePit({ position }) {
  const [x, , z] = position;
  
  return (
    <group position={[x, TERRAIN_Y, z]}>
      {/* Stone ring */}
      <mesh position={[0, 0.2, 0]} receiveShadow>
        <torusGeometry args={[1.5, 0.3, 8, 12]} />
        <meshStandardMaterial color="#5a5a5a" roughness={0.9} />
      </mesh>
      {/* Fire (simple representation) */}
      <mesh position={[0, 0.5, 0]}>
        <coneGeometry args={[0.8, 1.5, 6]} />
        <meshStandardMaterial 
          color="#ff6600" 
          emissive="#ff3300" 
          emissiveIntensity={0.8}
          transparent
          opacity={0.9}
        />
      </mesh>
      {/* Inner fire */}
      <mesh position={[0, 0.3, 0]}>
        <coneGeometry args={[0.5, 1, 5]} />
        <meshStandardMaterial 
          color="#ffaa00" 
          emissive="#ff6600" 
          emissiveIntensity={1}
        />
      </mesh>
    </group>
  );
}

/**
 * Render all objects for a single chunk
 */
function ChunkObjects({ chunk }) {
  const objects = chunk.objects || [];
  
  return (
    <group>
      {objects.map((obj, index) => {
        const key = `${chunk.key}-${obj.type}-${index}`;
        
        switch (obj.type) {
          case 'road':
            return (
              <WorldRoad
                key={key}
                from={{ x: obj.from.x, z: obj.from.z }}
                to={{ x: obj.to.x, z: obj.to.z }}
                width={4}
              />
            );
          
          case 'gate':
            return (
              <TownGate
                key={key}
                position={[obj.position?.x || 0, TERRAIN_Y, obj.position?.z || 0]}
                rotation={obj.rotation || 0}
              />
            );
          
          case 'frost-gate':
            return (
              <FrostGate
                key={key}
                position={[obj.position?.x || 0, TERRAIN_Y, obj.position?.z || 0]}
                rotation={obj.rotation || 0}
              />
            );
          
          case 'standing-stones':
            return (
              <StandingStonesLandmark
                key={key}
                position={[obj.position.x, TERRAIN_Y, obj.position.z]}
              />
            );
          
          case 'tree':
            return <SimpleTree key={key} position={[obj.position.x, TERRAIN_Y, obj.position.z]} />;
          
          case 'snow-tree':
            return <SnowTree key={key} position={[obj.position.x, TERRAIN_Y, obj.position.z]} />;
          
          case 'rock':
            return <SimpleRock key={key} position={[obj.position.x, TERRAIN_Y, obj.position.z]} />;
          
          case 'snow-rock':
            return <SnowRock key={key} position={[obj.position.x, TERRAIN_Y, obj.position.z]} />;
          
          case 'ruined-watchtower':
            return <RuinedWatchtower key={key} position={[obj.position.x, TERRAIN_Y, obj.position.z]} />;
          
          case 'stone-bridge':
            return <StoneBridge key={key} position={[obj.position?.x || 0, TERRAIN_Y, obj.position?.z || 0]} />;
          
          case 'river':
            return <River key={key} from={obj.from} to={obj.to} />;
          
          case 'abandoned-camp':
            return <AbandonedCamp key={key} position={[obj.position.x, TERRAIN_Y, obj.position.z]} />;
          
          case 'rune-stone':
            return <RuneStone key={key} position={[obj.position.x, TERRAIN_Y, obj.position.z]} />;
          
          case 'frost-longhouse':
            return <FrostLonghouse key={key} position={[obj.position.x, TERRAIN_Y, obj.position.z]} rotation={obj.rotation || 0} />;
          
          case 'frost-hall':
            return <FrostHall key={key} position={[obj.position.x, TERRAIN_Y, obj.position.z]} rotation={obj.rotation || 0} />;
          
          case 'frost-house':
            return <FrostHouse key={key} position={[obj.position.x, TERRAIN_Y, obj.position.z]} rotation={obj.rotation || 0} />;
          
          case 'fire-pit':
            return <FirePit key={key} position={[obj.position.x, TERRAIN_Y, obj.position.z]} />;
          
          default:
            return null;
        }
      })}
    </group>
  );
}

/**
 * ChunkStreamer component
 * Manages terrain AND objects for active chunks around player
 */
export default function ChunkStreamer({ playerPosition }) {
  const playerX = playerPosition?.x || 0;
  const playerZ = playerPosition?.z || 0;
  
  // Get active chunks (5x5 grid around player with radius=2)
  const activeChunks = useMemo(() => {
    return getActiveChunks(playerX, playerZ);
  }, [playerX, playerZ]);
  
  const CHUNK_HALF = CHUNK_SIZE / 2; // 32
  
  return (
    <group>
      {activeChunks.map(chunk => {
        const chunkCenterX = chunk.chunkX * CHUNK_SIZE;
        const chunkCenterZ = chunk.chunkZ * CHUNK_SIZE;
        
        // Calculate chunk extent
        const chunkExtentX = Math.abs(chunkCenterX) + CHUNK_HALF;
        const chunkExtentZ = Math.abs(chunkCenterZ) + CHUNK_HALF;
        
        // Only skip terrain if ENTIRE chunk is inside Thornhaven
        const skipTerrain = (chunkExtentX <= THORNHAVEN_HALF) && (chunkExtentZ <= THORNHAVEN_HALF);
        
        return (
          <group key={chunk.key}>
            {/* Chunk terrain with biome-based coloring */}
            {!skipTerrain && (
              <ChunkTerrain 
                chunkX={chunk.chunkX} 
                chunkZ={chunk.chunkZ}
                biome={chunk.biome}
              />
            )}
            
            {/* Chunk objects */}
            <ChunkObjects chunk={chunk} />
          </group>
        );
      })}
    </group>
  );
}

/**
 * Hook to get current chunk info
 */
export function useCurrentChunk(playerPosition) {
  return useMemo(() => {
    if (!playerPosition) return null;
    const key = `${Math.floor(playerPosition.x / CHUNK_SIZE)},${Math.floor(playerPosition.z / CHUNK_SIZE)}`;
    return WORLD_CHUNKS[key] || null;
  }, [playerPosition?.x, playerPosition?.z]);
}
